import { SearchOutlined } from '@ant-design/icons';
import {
	Button,
	Col,
	Input,
	Modal,
	Row,
	Space,
	TreeSelect,
	Tooltip,
	// Space,
} from 'antd';
import ClearFilter from 'components/Global/ClearFilter';
import PayPeriodFilter from 'components/Global/PayPeriodFilter';
import { FC, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { postApi, putApiParams } from 'redux/apis';
import {
	checkPermission,
	createModifiedArray,
	getOptionLabel,
	invalidText,
	toastText,
} from 'utils/utils';
import BatchEditModal from '../BatchEditModal';
import styles from './index.module.scss';
import './index.scss';
import { SearchAndFilterProps } from './types';
import { CloseSvg } from 'utils/svgs';
import { useSearchParams } from 'react-router-dom';
import BatchDeleteModal from '../BatchDeleteModal';
// For search filter and paginate
const SearchAndFilter: FC<SearchAndFilterProps> = (props) => {
	const {
		searchValue,
		performFilterHandler,
		filterValue,
		onChangeSearchValue,
		onChangePayPeriod,
		selectedPayPeriod,
		clearFilter,
		isBatchEdit,
		selectedRowKeys,
		isAllSelected,
		initialCall,
		changePayPeriodBatchEdit,
	} = props;

	const { optionData: customer } = useSelector((state: any) => state.customer);
	// const { data: customer } = useSelector((state: any) => state.customer);
	const { optionData: classList } = useSelector((state: any) => state.class);
	const { data: employees } = useSelector((state: any) => state.employees);
	const { data: configurations } = useSelector(
		(state: any) => state?.configuration
	);
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [queryParams, setQueryParams] = useSearchParams();
	// configurationsData?.isCustomerRequiredForJournal;
	// const { data: configurationsData } = useSelector(
	// 	(state: any) => state?.configuration
	// );

	const [filteredClassList, setFilteredClassList] = useState(classList);
	const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
	const [isBatchDeleteModalOpen, setIsBatchDeleteModalOpen] = useState(false);

	const [isClassEdit, setIsClassEdit] = useState(false);
	const [isCustomerEdit, setIsCustomerEdit] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [batchValues, setBatchValues] = useState({
		classId: null,
		className: null,
		customerId: null,
		customerName: null,
	});
	const [error, setError] = useState(false);
	const [deleteOption, setDeleteOption] = useState(false);

	// Local state for immediate UI updates
	// TreeSelect with multiple always returns an array
	const [localEmployeeIds, setLocalEmployeeIds] = useState<string[]>(
		(filterValue?.employeeId as string[]) || []
	);
	const [localCustomerIds, setLocalCustomerIds] = useState<string[]>(
		(filterValue?.customerId as string[]) || []
	);
	const [localClassIds, setLocalClassIds] = useState<string[]>(
		(filterValue?.classId as string[]) || []
	);
	const [employeesTooltipDisabled, setEmployeesTooltipDisabled] = useState(false);
	const [customersTooltipDisabled, setCustomersTooltipDisabled] = useState(false);
	const [classesTooltipDisabled, setClassesTooltipDisabled] = useState(false);

	// Debounce refs for filters
	const employeeDebounceRef = useRef<NodeJS.Timeout | null>(null);
	const customerDebounceRef = useRef<NodeJS.Timeout | null>(null);
	const classDebounceRef = useRef<NodeJS.Timeout | null>(null);

	// Sync local state with filterValue when it changes externally (e.g., clear filter)
	// TreeSelect with multiple always returns an array
	useEffect(() => {
		setLocalEmployeeIds((filterValue?.employeeId as string[]) || []);
	}, [filterValue?.employeeId]);

	useEffect(() => {
		// Transform 'blank-customer' back to empty strings for display
		const customerIds = (filterValue?.customerId as string[]) || [];
		const transformedCustomerIds = customerIds.map((id: string) =>
			id === 'blank-customer' ? '' : id
		);
		setLocalCustomerIds(transformedCustomerIds);
	}, [filterValue?.customerId]);

	useEffect(() => {
		// Transform 'blank-class' back to empty strings for display
		const classIds = (filterValue?.classId as string[]) || [];
		const transformedClassIds = classIds.map((id: string) =>
			id === 'blank-class' ? '' : id
		);
		setLocalClassIds(transformedClassIds);
	}, [filterValue?.classId]);

	// Debounced handlers for filters
	const debouncedSetEmployees = useCallback(
		(employeeIds: string[]) => {
			setLocalEmployeeIds(employeeIds); // Update UI immediately
			if (employeeDebounceRef.current) {
				clearTimeout(employeeDebounceRef.current);
			}
			employeeDebounceRef.current = setTimeout(() => {
				performFilterHandler('employeeId', employeeIds);
			}, 500);
		},
		[performFilterHandler]
	);

	const debouncedSetCustomers = useCallback(
		(customerIds: string[]) => {
			setLocalCustomerIds(customerIds); // Update UI immediately
			if (customerDebounceRef.current) {
				clearTimeout(customerDebounceRef.current);
			}
			customerDebounceRef.current = setTimeout(() => {
				// Transform empty strings to 'blank-customer' for backend
				const transformedCustomerIds = customerIds.map((id: string) =>
					id === '' ? 'blank-customer' : id
				);
				performFilterHandler('customerId', transformedCustomerIds);
			}, 500);
		},
		[performFilterHandler]
	);

	const debouncedSetClasses = useCallback(
		(classIds: string[]) => {
			setLocalClassIds(classIds); // Update UI immediately
			if (classDebounceRef.current) {
				clearTimeout(classDebounceRef.current);
			}
			classDebounceRef.current = setTimeout(() => {
				// Transform empty strings to 'blank-class' for backend
				const transformedClassIds = classIds.map((id: string) =>
					id === '' ? 'blank-class' : id
				);
				performFilterHandler('classId', transformedClassIds);
			}, 500);
		},
		[performFilterHandler]
	);

	// Cleanup timeouts on unmount
	useEffect(() => {
		return () => {
			if (employeeDebounceRef.current) {
				clearTimeout(employeeDebounceRef.current);
			}
			if (customerDebounceRef.current) {
				clearTimeout(customerDebounceRef.current);
			}
			if (classDebounceRef.current) {
				clearTimeout(classDebounceRef.current);
			}
		};
	}, []);

	const handleChangeSearchValue = (value: any) => {
		onChangeSearchValue && onChangeSearchValue(value);
	};

	const handleCancelModal = () => {
		setIsClassEdit(false);
		setIsCustomerEdit(false);
		setDeleteOption(false);
		setError(false);
		setBatchValues({
			classId: null,
			className: null,
			customerId: null,
			customerName: null,
		});
		setIsBatchDeleteModalOpen(false);
		setIsBatchModalOpen(false);
	};

	const checkValidation = () => {
		let isValid = true;
		if (
			(configurations?.isClassRequiredForJournal &&
				invalidText(batchValues.classId) &&
				isClassEdit) ||
			(configurations?.isCustomerRequiredForJournal &&
				invalidText(batchValues.customerId) &&
				isCustomerEdit)
		) {
			setError(true);
			setIsBatchModalOpen(false);
			isValid = false;
		}
		return isValid;
	};

	const batchHandler = (name: string, value: string) => {
		// checkChangeValidation(name, value);
		setBatchValues((prev) => {
			return {
				...prev,
				[name]: value,
			};
		});
	};

	const batchDeleteHandler = async () => {
		// checkChangeValidation(name, value);
		setIsLoading(true);
		let requestBody;
		if (deleteOption) {
			requestBody = {
				payPeriodId: selectedPayPeriod,
				companyId: localStorage.getItem('companyId'),
			};
		} else {
			requestBody = {
				timeActivityIds: selectedRowKeys,
				companyId: localStorage.getItem('companyId'),
			};
		}

		try {
			const response: any = await postApi(
				'/time-activities/bulk-delete',
				requestBody
			);

			if (response.data.success) {
				setIsBatchDeleteModalOpen(false);
				toastText(
					`Time Activities Deleted Successfully. (Count:${response.data.deletedCount})`,
					'success'
				);
				initialCall();
			} else {
				setIsBatchDeleteModalOpen(false);
				toastText('Something went wrong in deleting activities', 'error');
			}
		} catch (err: any) {
			let message = 'Something went wrong in deleting activities';
			if (err?.response?.data?.message) {
				message = err.response.data.message;
			}
			toastText(message, 'error');
		} finally {
			setIsLoading(false);
		}
	};

	const closeWarningModal = () => {
		setIsClassEdit(false);
		setIsCustomerEdit(false);
		setBatchValues({
			classId: null,
			className: null,
			customerId: null,
			customerName: null,
		});
		setError(false);
	};

	const batchEditHandler = async () => {
		if (checkValidation()) {
			setIsLoading(true);
			try {
				const data: any = batchValues;
				if (isAllSelected) {
					data['isSelectedAll'] = isAllSelected;
				}
				if (selectedRowKeys.length > 0) {
					data['timeActivityIds'] = selectedRowKeys;
				}
				// if (data['classId'] === null) {
				// 	delete data['classId'];
				// 	delete data['className'];
				// }
				// if (data['customerId'] === null) {
				// 	delete data['customerId'];
				// 	delete data['customerName'];
				// }
				if (data['classId'] === '') {
					data['classId'] = null;
					data['className'] = null;
				}
				if (data['customerId'] === '') {
					data['customerId'] = null;
					data['customerName'] = null;
				}
				if (isClassEdit) {
					delete data['customerId'];
					delete data['customerName'];
				}
				if (isCustomerEdit) {
					delete data['classId'];
					delete data['className'];
				}

				// Prepare filter params with array to comma-separated string conversion
				const filterParams: any = {
					payPeriodId: selectedPayPeriod,
					companyId: localStorage.getItem('companyId'),
					...filterValue,
				};

				// Handle employeeId as array - convert to comma-separated string for API
				if (Array.isArray(filterParams.employeeId) && filterParams.employeeId.length > 0) {
					filterParams.employeeId = filterParams.employeeId.join(',');
				} else {
					delete filterParams.employeeId;
				}

				// Handle customerId as array - convert to comma-separated string for API
				if (Array.isArray(filterParams.customerId) && filterParams.customerId.length > 0) {
					filterParams.customerId = filterParams.customerId.join(',');
				} else {
					delete filterParams.customerId;
				}

				// Handle classId as array - convert to comma-separated string for API
				if (Array.isArray(filterParams.classId) && filterParams.classId.length > 0) {
					filterParams.classId = filterParams.classId.join(',');
				} else {
					delete filterParams.classId;
				}

				await putApiParams('/time-activities/batch', data, filterParams);
				toastText('All activities updated successfully', 'success');
				handleCancelModal();
				initialCall();
				// await dispatch(
				// 	getTimeLogs({
				// 		page: 1,
				// 		limit: 20,
				// 		payPeriodId: selectedPayPeriod,
				// 	})
				// );
			} catch (err) {
				toastText('Something went wrong in time activity update', 'error');
			} finally {
				setIsLoading(false);
			}
		}
	};

	useEffect(() => {
		if (
			configurations &&
			configurations.settings &&
			classList &&
			classList.length
		) {
			const _classList = createModifiedArray(
				classList,
				configurations.settings[0].fields['f1'].value
			);
			setFilteredClassList(_classList);
		}
	}, [configurations, classList]);

	// Filter customer data to remove "Select Customer" option and add "Blank Customer"
	const filteredCustomerData = useMemo(() => {
		if (!Array.isArray(customer)) {
			return [];
		}
		const filtered = customer.filter((item: any) => {
			// Filter out items with "Select Customer" title at parent level only
			return item?.title !== 'Select Customer';
		});
		// Add "Blank Customer" option at the beginning
		return [
			{
				title: 'Blank Customer',
				value: '',
				key: 'blank-customer',
				disabled: false,
				children: [],
			},
			...filtered,
		];
	}, [customer]);

	// Filter class data to remove "Select Class" option and add "Blank Class"
	const filteredClassData = useMemo(() => {
		if (!Array.isArray(filteredClassList)) {
			return [];
		}
		const filtered = filteredClassList.filter((item: any) => {
			// Filter out items with "Select Class" title at parent level only
			return item?.title !== 'Select Class';
		});
		// Add "Blank Class" option at the beginning
		return [
			{
				title: 'Blank Class',
				value: '',
				key: 'blank-class',
				disabled: false,
				children: [],
			},
			...filtered,
		];
	}, [filteredClassList]);

	// Transform employees to treeData format for TreeSelect
	const employeeTreeData = useMemo(() => {
		return employees?.map((employee: any) => ({
			title: employee.fullName,
			value: employee.id,
			key: employee.id,
			children: [], // Empty for flat list
		})) || [];
	}, [employees]);

	const selectedCompanyPermission = useSelector(
		(state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
	);

	const isEditTimeLogPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Time Logs',
		permission: ['edit'],
	});

	const isDeleteTimeLogPermission = checkPermission(selectedCompanyPermission, {
		permissionName: 'Time Logs',
		permission: ['delete'],
	});

	const showConfigurationHandler = () => {
		setIsClassEdit(false);
		setIsCustomerEdit(false);
		setError(false);
		setQueryParams({
			payPeriodId: selectedPayPeriod as string,
			openDrawer: 'true',
		});
		localStorage.setItem('settings', 'Configurations');
		localStorage.setItem('payPeriod', selectedPayPeriod as string);
	};

	// JSX
	return (
		<div className={styles['search-filter']}>
			<div className={styles['search-filter__wrapper']}>
				{/* <Row className={styles['custom-row']} align="middle" gutter={[16, 16]}>
					<Col className={styles['custom-col']}>
						<Space>
							<PayPeriodFilter
								payPeriodId={selectedPayPeriod}
								onChangePayPeriodId={(value) => {
									onChangePayPeriod(value);

									changePayPeriodBatchEdit();
								}}
							/>
							<Input
								width="100%"
								placeholder="Search here..."
								// style={{ fontSize: '12px' }}
								style={{ fontSize: '12px', minHeight: '40px' }}
								suffix={<SearchOutlined />}
								onChange={(e) => handleChangeSearchValue(e.target.value)}
								value={searchValue}
								size="large"
							/>
							<Select
								className={styles['search-filter__filter']}
								style={{ width: '100%' }}
								onChange={(value) => performFilterHandler('employeeId', value)}
								size="large"
								placeholder="Employee"
								value={filterValue?.employeeId}
								showSearch
								allowClear
								filterOption={(input, option: any) =>
									(option?.children as string)
										.toLowerCase()
										.includes(input.toLowerCase())
								}
							>
								<Select.Option value="">Select Employee</Select.Option>

								{employees?.map((employee: any, index: number) => (
									<Select.Option value={employee?.id} key={index}>
										{employee?.fullName}
									</Select.Option>
								))}
							</Select>
							<TreeSelect
								className={styles['search-filter__tree-filter']}
								showSearch
								style={{ width: '100%' }}
								value={filterValue?.customerId}
								placeholder="Customer"
								allowClear
								treeDefaultExpandAll
								onChange={(value) => performFilterHandler('customerId', value)}
								size="large"
								treeData={customer}
								filterTreeNode={(inputValue, treeNode) =>
									treeNode.props.title

										.toLowerCase()

										.includes(inputValue.toLowerCase())
								}
								treeLine
							/>
							<TreeSelect
								className={styles['search-filter__tree-filter']}
								showSearch
								style={{ width: '100%' }}
								value={filterValue?.classId}
								placeholder="Class"
								allowClear
								treeDefaultExpandAll
								onChange={(value) => performFilterHandler('classId', value)}
								size="large"
								treeData={filteredClassList}
								filterTreeNode={(inputValue, treeNode) =>
									treeNode.props.title

										.toLowerCase()

										.includes(inputValue.toLowerCase())
								}
								treeLine
							/>
							<div className={styles['scaled-clear-filter']}>
								<ClearFilter clearFilter={clearFilter} />
							</div>{' '}
						</Space>
					</Col> */}

				{/* <Col span={4} className={styles['custom-col']}>
						{!isAccountClosed &&
							isCreateTimeSheetPermission &&
							!timeSheetData && (
								<Button
									className={styles['timesheet']}
									onClick={openValidateTimeSheetModalHandler}
								>
									Create Timesheet
								</Button>
							)}

						{!isAccountClosed &&
							isUpdateTimeSheetPermission &&
							timeSheetData && (
								<Button
									className={styles['timesheet']}
									onClick={openValidateTimeSheetModalHandler}
								>
									Update Timesheet
								</Button>
							)}
					</Col> */}
				{/* </Row> */}
				<Row
					className={styles['custom-row']}
					align="middle"
					justify="space-between"
				>
					<Col>
						<Space wrap>
							<PayPeriodFilter
								payPeriodId={selectedPayPeriod}
								onChangePayPeriodId={(value) => {
									onChangePayPeriod(value);
									changePayPeriodBatchEdit();
								}}
							/>

							<Input
								placeholder="Search here..."
								style={{ fontSize: '12px', minWidth: 200, height: 40 }}
								suffix={<SearchOutlined />}
								onChange={(e) => handleChangeSearchValue(e.target.value)}
								value={searchValue}
								size="large"
							/>

							<TreeSelect
								placeholder="Employees"
								value={localEmployeeIds}
								onChange={debouncedSetEmployees}
								showSearch
								allowClear
								multiple
								maxTagCount={0}
								size="large"
								style={{ minWidth: 200, fontSize: '12px', height: 40 }}
								treeData={employeeTreeData}
								treeLine
								treeDefaultExpandAll
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								onDropdownVisibleChange={(open) => {
									setEmployeesTooltipDisabled(open);
								}}
								maxTagPlaceholder={() => {
									const selectedEmployeeIds = localEmployeeIds;

									if (selectedEmployeeIds.length === 0) return 'Employees';

									const employeeLabels = selectedEmployeeIds.map((id: string) => {
										const employee = employees?.find((emp: any) => emp.id === id);
										return employee?.fullName || id;
									});

									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{employeeLabels.map((label: string, i: number) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== employeeLabels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{label}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={employeesTooltipDisabled ? false : undefined}
											trigger={employeesTooltipDisabled ? [] : ['hover']}
										>
											Employees ({selectedEmployeeIds.length})
										</Tooltip>
									);
								}}
							/>

							<TreeSelect
								placeholder="Customers"
								value={localCustomerIds}
								onChange={debouncedSetCustomers}
								size="large"
								allowClear
								multiple
								maxTagCount={0}
								treeDefaultExpandAll
								showSearch
								treeData={filteredCustomerData}
								style={{ minWidth: 200, fontSize: '12px', height: 40 }}
								filterTreeNode={(inputValue, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(inputValue.toLowerCase())
								}
								treeLine
								onDropdownVisibleChange={(open) => {
									setCustomersTooltipDisabled(open);
								}}
								maxTagPlaceholder={() => {
									const selectedCustomerIds = localCustomerIds;

									if (selectedCustomerIds.length === 0) return 'Customers';

									const customerLabels = selectedCustomerIds.map((id: string) => {
										return id === '' ? 'Blank Customer' : getOptionLabel(id, filteredCustomerData) || id;
									});

									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{customerLabels.map((label: string, i: number) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== customerLabels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{label}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={customersTooltipDisabled ? false : undefined}
											trigger={customersTooltipDisabled ? [] : ['hover']}
										>
											Customers ({selectedCustomerIds.length})
										</Tooltip>
									);
								}}
							/>

							<TreeSelect
								placeholder="Classes"
								value={localClassIds}
								onChange={debouncedSetClasses}
								size="large"
								allowClear
								multiple
								maxTagCount={0}
								treeDefaultExpandAll
								showSearch
								treeData={filteredClassData}
								style={{ minWidth: 200, fontSize: '12px', height: 40 }}
								filterTreeNode={(input, treeNode) =>
									treeNode?.props?.title
										?.toLowerCase()
										.includes(input.toLowerCase())
								}
								treeLine
								onDropdownVisibleChange={(open) => {
									setClassesTooltipDisabled(open);
								}}
								maxTagPlaceholder={() => {
									const selectedClassIds = localClassIds;

									if (selectedClassIds.length === 0) return 'Classes';

									const classLabels = selectedClassIds.map((id: string) => {
										return id === '' ? 'Blank Class' : getOptionLabel(id, filteredClassData) || id;
									});

									return (
										<Tooltip
											title={
												<div className="filter-tooltip-content">
													{classLabels.map((label: string, i: number) => (
														<div
															key={i}
															className="filter-tooltip-item"
															style={{
																borderBottom:
																	i !== classLabels.length - 1
																		? '1px solid rgba(52, 71, 53, 0.1)'
																		: 'none',
															}}
														>
															{label}
														</div>
													))}
												</div>
											}
											placement="bottom"
											overlayClassName="filter-tooltip"
											visible={classesTooltipDisabled ? false : undefined}
											trigger={classesTooltipDisabled ? [] : ['hover']}
										>
											Classes ({selectedClassIds.length})
										</Tooltip>
									);
								}}
							/>

							<div className={styles['scaled-clear-filter']}>
								<ClearFilter clearFilter={clearFilter} />
							</div>
						</Space>
					</Col>

					{/* <Col
						span={4}
						className={styles['custom-col']}
						style={{ textAlign: 'right' }}
					>
						{!isAccountClosed &&
							isCreateTimeSheetPermission &&
							!timeSheetData && (
								<Button
									className={styles['timesheet']}
									onClick={openValidateTimeSheetModalHandler}
								>
									Create Timesheet
								</Button>
							)}

						{!isAccountClosed &&
							isUpdateTimeSheetPermission &&
							timeSheetData && (
								<Button
									className={styles['timesheet']}
									onClick={openValidateTimeSheetModalHandler}
								>
									Update Timesheet
								</Button>
							)}
					</Col> */}
				</Row>

				{/* <Col className={styles['custom-col']}>

	<Space>

		<Input

			className={styles['search-filter__search']}

			placeholder="Search here..."

			suffix={<SearchOutlined />}

			onChange={(e) => {

				handleChangeSearchValue(e.target.value);

			}}

			value={searchValue}

			size="large"

		/>

		<Select

			className={styles['search-filter__filter']}

			style={{ width: 200 }}

			onChange={(value) => performFilterHandler('employeeId', value)}

			size="large"

			placeholder="Employee"

			value={filterValue?.employeeId}

			showSearch

			filterOption={(input, option: any) =>

				(option?.children as string)

					.toLowerCase()

					.indexOf(input.toLowerCase()) >= 0

			}

		>

			<Select.Option value="">Select Employee</Select.Option>

			{employees?.map((employee: any, index: number) => {

				return (

					<Select.Option

						value={employee?.id}

						key={index}

					>

						{employee?.fullName}

					</Select.Option>

				);

			})}

		</Select>



		<TreeSelect

			className={styles['search-filter__tree-filter']}

			showSearch

			style={{ width: 200 }}

			value={filterValue?.customerId}

			// dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}

			placeholder="Customer"

			allowClear={true}

			treeDefaultExpandAll

			onChange={(value) => {

				performFilterHandler('customerId', value);

			}}

			size="large"

			treeData={customer}

			filterTreeNode={(inputValue, treeNode) => {

				// Customize the filtering logic here

				return treeNode.props.title

					.toLowerCase()

					.includes(inputValue.toLowerCase());

			}}

			// This makes dropdown items render with a span you can style

			treeLine={true}

			// Custom dropdown render (optional, if styling needs control)

		/>

		<TreeSelect

			showSearch

			className={styles['search-filter__tree-filter']}

			style={{ width: 200 }}

			onChange={(value) => {

				performFilterHandler('classId', value);

			}}

			value={filterValue?.classId}

			placeholder="Class"

			allowClear={true}

			treeDefaultExpandAll

			size="large"

			treeData={filteredClassList}

			filterTreeNode={(inputValue, treeNode) => {

				// Customize the filtering logic here

				return treeNode.props.title

					.toLowerCase()

					.includes(inputValue.toLowerCase());

			}}

			treeLine={true}

		/>

		<PayPeriodFilter

			payPeriodId={selectedPayPeriod}

			onChangePayPeriodId={(value) => {

				onChangePayPeriod(value);

				changePayPeriodBatchEdit();

			}}

		/>

		<ClearFilter clearFilter={clearFilter} />

	</Space>

</Col> */}

				{isBatchEdit && (
					<Row
						justify="start"
						gutter={[16, 16]}
						style={{ marginTop: 12 }}
					>
						<Col>
							<Button
								className={styles['batch-edit']}
								onClick={() => {
									setIsBatchModalOpen(true);

									setIsClassEdit(true);
								}}
								disabled={!isEditTimeLogPermission}
							>
								Batch Edit
							</Button>
						</Col>
						<Col>
							<Button
								className={`${styles['batch-edit']} ${styles['margin-top']}`}
								onClick={() => {
									setIsBatchDeleteModalOpen(true);

									setDeleteOption(false);

									setIsClassEdit(true);
								}}
								disabled={!isDeleteTimeLogPermission}
							>
								Batch Delete
							</Button>
						</Col>
					</Row>
				)}
			</div>
			<BatchEditModal
				isModalOpen={isBatchModalOpen}
				handleCancel={handleCancelModal}
				batchValues={batchValues}
				classList={filteredClassList}
				customerList={customer}
				batchHandler={batchHandler}
				batchEditHandler={batchEditHandler}
				error={error}
				isClassEdit={isClassEdit}
				isCustomerEdit={isCustomerEdit}
				isLoading={isLoading}
				setIsClassEdit={setIsClassEdit}
				setIsCustomerEdit={setIsCustomerEdit}
			/>

			<BatchDeleteModal
				isModalOpen={isBatchDeleteModalOpen}
				handleCancel={handleCancelModal}
				deleteOptionValue={deleteOption}
				handelChange={setDeleteOption}
				batchDeleteHandler={batchDeleteHandler}
				currentPayPeriod={selectedPayPeriod}
				isLoading={isLoading}
				error={error}
			/>

			<Modal
				open={error}
				onCancel={closeWarningModal}
				closable={false}
				footer={null}
				width={600}
				className="time-sheet-modal profile__popup"
			>
				<Row className={styles['time-sheet-modal__header']}>
					<Col span={22}>
						<div className="userDetailsTitle">
							<b>
								{`As per configuration ${isClassEdit ? 'Class' : 'Customer'
									} is mandatory.Please
								update the configuration.`}
							</b>
						</div>
					</Col>
					<div
						className={styles['time-sheet-modal__header-delete']}
						onClick={closeWarningModal}
						style={{
							display: 'flex',
							justifyContent: 'center',
							marginTop: '10px',
						}}
					>
						<CloseSvg />
					</div>
				</Row>
				<div className="time_sheet_modal_footer">
					<Row
						justify="start"
						className={styles['time-sheet-modal__body__buttons']}
					// gutter={16}
					>
						<Col span={8}>
							<Button
								className={`${styles['time-sheet-modal__body__send-email']} ${isLoading && 'pointer-event-none'
									}`}
								block={true}
							// onClick={batchEditHandler}
							>
								{isLoading ? (
									<img
										src="/assets/gifs/loading-black.gif"
										height={40}
									/>
								) : (
									<div
										className={
											styles['time-sheet-modal__body__send-email-inside']
										}
										onClick={() => {
											showConfigurationHandler();
										}}
									>
										<p>Configuration</p>
									</div>
								)}
							</Button>
						</Col>
						<Col span={8}>
							<Button
								className={`${styles['time-sheet-modal__body__send-email']}`}
								block={true}
								onClick={closeWarningModal}
							>
								Cancel
							</Button>
						</Col>
					</Row>
				</div>
			</Modal>
		</div>
	);
};

export default SearchAndFilter;
